import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Pencil, Trash2, Plus } from "lucide-react";

const empty = { title: "", description: "", date: "", location: "", image_url: "" };

export default function AdminEvents() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setF(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setF({ ...empty, ...it }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await api.put(`/events/${editing.id}`, f);
        toast.success("Event updated");
      } else {
        await api.post("/events", f);
        toast.success("Event created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-events-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="overline text-terracotta mb-2">Content</p>
          <h1 className="font-heading font-black text-4xl tracking-tighter text-olive">Events</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none" data-testid="new-event-btn">
              <Plus size={16} className="mr-1" /> New Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit event" : "New event"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="event-title" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea required rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="event-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="event-date" />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input required value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="event-location" />
                </div>
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input value={f.image_url || ""} onChange={(e) => setF({ ...f, image_url: e.target.value })} data-testid="event-image" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={loading} className="bg-olive hover:bg-olive-hover text-bone rounded-none" data-testid="event-save">
                  {loading ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream border-b border-border">
            <tr className="text-left">
              <th className="p-4 font-semibold">Title</th>
              <th className="p-4 font-semibold">Date</th>
              <th className="p-4 font-semibold">Location</th>
              <th className="p-4 font-semibold w-36">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-muted_ink">No events yet</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-b border-border last:border-0" data-testid={`event-row-${it.id}`}>
                <td className="p-4 font-medium text-ink">{it.title}</td>
                <td className="p-4 text-muted_ink">{it.date}</td>
                <td className="p-4 text-muted_ink">{it.location}</td>
                <td className="p-4 flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}><Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-destructive hover:text-destructive"><Trash2 size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
